import { getToken, clearLocalDatas } from '@/utils/auth';
import { logout } from '@/store/actions';
import { message } from 'antd';
import store from '@/store';

/**
 * Token管理工具
 * 提供token的解析、验证、刷新等功能
 */

// 解析JWT token
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Token解析失败:', error);
    return null;
  }
};

// 检查token是否过期
export const isTokenExpired = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

// 检查token是否即将过期（默认提前5分钟）
export const isTokenNearExpiry = (token, bufferMinutes = 5) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const bufferTime = bufferMinutes * 60; // 转换为秒
  return payload.exp < (currentTime + bufferTime);
};

// 获取token剩余时间（秒）
export const getTokenTimeRemaining = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return 0;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return Math.max(0, payload.exp - currentTime);
};

// 格式化剩余时间
export const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return '已过期';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}小时${minutes}分钟`;
  } else if (minutes > 0) {
    return `${minutes}分钟${remainingSeconds}秒`;
  } else {
    return `${remainingSeconds}秒`;
  }
};

// Token自动刷新管理器
class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.checkInterval = 300000; // 每5分钟检查一次，降低频率
  }

  // 开始token监控
  startTokenMonitoring() {
    this.stopTokenMonitoring(); // 先停止之前的监控
    
    this.refreshTimer = setInterval(() => {
      this.checkTokenExpiry();
    }, this.checkInterval);
    
    // 立即检查一次
    this.checkTokenExpiry();
  }

  // 停止token监控
  stopTokenMonitoring() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // 检查token过期状态
  checkTokenExpiry() {
    const token = getToken();
    if (!token) {
      this.stopTokenMonitoring();
      return;
    }

    const isExpired = isTokenExpired(token);
    const timeRemaining = getTokenTimeRemaining(token);

    if (isExpired) {
      this.handleTokenExpired();
    } else if (timeRemaining <= 600) { // 小于10分钟时提醒
      this.handleTokenNearExpiry(timeRemaining);
    }
  }

  // 处理token过期
  handleTokenExpired() {
    this.stopTokenMonitoring();
    message.error('登录已过期，请重新登录');
    
    // 清除token并执行登出
    clearLocalDatas(['token']);
    store.dispatch(logout());
    
    // 延迟重定向到登录页
    setTimeout(() => {
      window.location.hash = '/login';
      window.location.reload();
    }, 1000);
  }

  // 处理token即将过期
  handleTokenNearExpiry(timeRemaining) {
    if (timeRemaining > 300) { // 大于5分钟时只提醒一次
      message.warning(`登录将在${formatTimeRemaining(timeRemaining)}后过期`, 5);
    } else if (timeRemaining > 60) { // 1-5分钟时提醒
      message.warning(`登录将在${formatTimeRemaining(timeRemaining)}后过期，请保存工作`, 3);
    } else { // 小于1分钟时执行登出
      message.error(`登录即将过期，正在执行登出...`, 2);
      this.handleTokenExpired();
    }
  }

  // 获取当前token信息
  getTokenInfo() {
    const token = getToken();
    if (!token) return null;

    const payload = decodeToken(token);
    const timeRemaining = getTokenTimeRemaining(token);
    
    return {
      token,
      payload,
      isExpired: isTokenExpired(token),
      isNearExpiry: isTokenNearExpiry(token),
      timeRemaining,
      timeRemainingFormatted: formatTimeRemaining(timeRemaining)
    };
  }
}

// 创建全局token管理器实例
const tokenManager = new TokenManager();

export default tokenManager; 